import { useMemo, useState } from "react";
import { useField } from "formik";
import { parsePhoneNumberWithError, type PhoneNumber } from "libphonenumber-js";

import { useTouchFieldOnSubmit } from "@vega-core/helpers/formik.helper";
import { isTruthy } from "@vega-core/utils/isTruthy";

import { PhoneInput } from "../Controls/PhoneInput/PhoneInput";

const stripToBeFormat = (rawValue: string): string => {
  const digitsAndPlus = rawValue.replace(/[^\d+]/g, "");

  if (!digitsAndPlus.startsWith("+")) return digitsAndPlus;

  return `+${digitsAndPlus.slice(1).replace(/\+/g, "")}`;
};

export function normalizeToE164(rawValue: string): string {
  if (!rawValue) return rawValue;

  try {
    return parsePhoneNumberWithError(rawValue).format("E.164");
  } catch {
    return stripToBeFormat(rawValue);
  }
}

type Props = {
  isOptional?: boolean;
  name: string;
  hideLabel?: boolean;
  label?: string;
  canCopyToAllPhones?: boolean;
  onCopyAllPhones?: (phone: string) => void;
  fullWidth?: boolean;
  constrainInputWidth?: boolean;
};
const DEFAULT_CODE = "+1";

export const NANP_COUNTRIES = [
  "BS", // Bahamas
  "BB", // Barbados
  "AI", // Anguilla
  "AG", // Antigua and Barbuda
  "VG", // British Virgin Islands
  "VI", // U.S. Virgin Islands
  "KY", // Cayman Islands
  "BM", // Bermuda
  "GD", // Grenada
  "TC", // Turks and Caicos Islands
  "MS", // Montserrat
  "MP", // Northern Mariana Islands
  "GU", // Guam
  "AS", // American Samoa
  "SX", // Sint Maarten
  "LC", // Saint Lucia
  "DM", // Dominica
  "VC", // Saint Vincent and the Grenadines
  "PR", // Puerto Rico
  "DO", // Dominican Republic
  "TT", // Trinidad and Tobago
  "KN", // Saint Kitts and Nevis
  "JM", // Jamaica
];

function getDialingPrefix(number: PhoneNumber) {
  try {
    const { country, countryCallingCode, nationalNumber } = number;

    if (
      country &&
      countryCallingCode === "1" &&
      NANP_COUNTRIES.includes(country)
    ) {
      const areaCode = nationalNumber.slice(0, 3);
      const trimmedNationalNumber = nationalNumber.slice(3);

      return [`+${countryCallingCode}-${areaCode}`, trimmedNationalNumber];
    }

    return [`+${countryCallingCode}`, nationalNumber];
  } catch (e) {
    console.log("Error parsing phone number:", e);

    return [DEFAULT_CODE, ""];
  }
}

export function PhoneField({
  isOptional,
  name,
  label,
  hideLabel,
  canCopyToAllPhones,
  onCopyAllPhones,
  fullWidth,
  constrainInputWidth = false,
}: Props) {
  const [field, meta, helpers] = useField<string | undefined>(name);
  useTouchFieldOnSubmit(name);

  const [phoneCode, setPhoneCode] = useState(DEFAULT_CODE);

  const parsedPhoneNumber = useMemo(() => {
    if (!field.value || field.value.length <= 1) {
      return null;
    }

    try {
      const parsed = parsePhoneNumberWithError(field.value);
      const countryCallingCode = getDialingPrefix(parsed);

      if (countryCallingCode[0] !== phoneCode) {
        setPhoneCode(countryCallingCode[0]);
      }

      return countryCallingCode[1];
    } catch {
      // Ignore parse errors
    }

    return null;
  }, [field.value, phoneCode]);

  const phoneNumber = useMemo(() => {
    if (parsedPhoneNumber) {
      return parsedPhoneNumber;
    }

    if (!field.value) {
      return "";
    }

    if (field.value.startsWith(phoneCode)) {
      return field.value.slice(phoneCode.length);
    }

    return field.value;
  }, [field.value, parsedPhoneNumber, phoneCode]);

  return (
    <PhoneInput
      canCopyToAllPhones={canCopyToAllPhones}
      constrainInputWidth={constrainInputWidth}
      errorsPhoneNumber={meta.touched ? meta.error : undefined}
      fullWidth={fullWidth}
      handleBlur={() => helpers.setTouched(true)}
      hideLabel={hideLabel}
      isOptional={isOptional}
      label={label}
      onCopyAllPhones={onCopyAllPhones}
      phoneCode={phoneCode}
      phoneNumber={phoneNumber}
      setFieldValue={(fieldName, value) => {
        if (fieldName === "phoneCode") {
          setPhoneCode(value);
          helpers.setValue(
            isTruthy(phoneNumber)
              ? normalizeToE164(value + phoneNumber)
              : undefined,
          );
        } else if (fieldName === "phoneNumber") {
          helpers.setValue(
            isTruthy(value) ? normalizeToE164(phoneCode + value) : undefined,
          );
        }
      }}
      touchedPhoneNumber={meta.touched}
    />
  );
}
